
import { Vue, Component } from "vue-property-decorator";
import Pagination from "@/components/Pagination/index.vue";
import { formatDateOrTime } from "@/utils";
import { queryPendingProcessing } from "@/api/proforma";
import { AppModule } from "@/store/modules/app";

interface ModelItem {
  typeName: string;
  number: string;
}

interface OverOrOweRow {
  customerCode: string;
  customerAbbr: string;
  piCode: string;
  seller: string;
  sellerAssistant: string;
  hqTransactionNumber: string;
  hqProductName: string;
  /** 发货数量 */
  declarationQty: number;
  /** PI 接单数量 */
  shipmentQty: number;
  /** 收到数量 */
  deliveryQty: number;
  /** 下单数量 */
  orderQty: number;
  /** 超欠发数量 */
  exceptionQty: number;
  purchaser: string;
  purchaseOrderCode: string;
  supplierAbbr: string;
  /** 异常计划创建时间 */
  exceptionPlanCreatedTime: string;
  poInfoId: string;
  overUnderageStatus: string;
  models?: ModelItem[];
}

@Component({
  name: "OverOrOweList",
  components: {
    Pagination,
    Quantity: () =>
      import(
        "@/views-oms/sales-order-management/order/abnormal-delivery-handling/components/Quantity.vue"
      ),
  },
})
export default class extends Vue {
  private loading = false;
  private piKeyword = "";
  private sortDesc = true;
  private modelDialogVisible = false;
  private showQuantity = false;
  private currentRow: Partial<OverOrOweRow> = {};
  private selectedItem: Partial<OverOrOweRow> = {};
  private table = {
    total: 0,
    pageNumber: 1,
    pageSize: 20,
    list: [] as OverOrOweRow[],
  };

  created() {
    this.getList();
  }

  private formatDateOrTime = formatDateOrTime;

  get tableSort() {
    return {
      prop: "exceptionPlanCreatedTime",
      order: this.sortDesc ? "descending" : "ascending",
    };
  }

  private headerCellStyle() {
    return {
      textAlign: "center",
    };
  }

  private cellStyle() {
    return {
      textAlign: "center",
    };
  }

  private qtyCellClassName({
    row,
    column,
  }: {
    row: OverOrOweRow;
    column: { property?: string };
  }) {
    if (column.property !== "exceptionQty") {
      return "";
    }
    if (row.overUnderageStatus === "OVERDELIVERY") {
      return "qty-col--over";
    }
    if (row.overUnderageStatus === "UNDERDELIVERY") {
      return "qty-col--owe";
    }
    return "";
  }

  private formatPair(left?: string | number, right?: string | number) {
    if (left === undefined || left === null || left === "") {
      return right ?? "";
    }
    if (right === undefined || right === null || right === "") {
      return left;
    }
    return `${left}/${right}`;
  }

  private formatSellerPair(seller?: string, assistant?: string) {
    const left = seller && String(seller).trim() ? String(seller).trim() : "-";
    const right =
      assistant && String(assistant).trim() ? String(assistant).trim() : "-";
    return `${left}/${right}`;
  }

  private formatExceptionQty(row: OverOrOweRow) {
    const qty = Math.abs(Number(row.exceptionQty) || 0);
    if (row.overUnderageStatus === "OVERDELIVERY") {
      return `+${qty}`;
    }
    if (row.overUnderageStatus === "UNDERDELIVERY") {
      return `-${qty}`;
    }
    return String(row.exceptionQty ?? "");
  }

  private handleSearch() {
    this.table.pageNumber = 1;
    this.getList();
  }

  private handleSortChange({ prop, order }: { prop?: string; order?: string }) {
    if (prop !== "exceptionPlanCreatedTime" || !order) {
      return;
    }
    const nextDesc = order === "descending";
    if (nextDesc === this.sortDesc) {
      return;
    }
    this.sortDesc = nextDesc;
    this.table.pageNumber = 1;
    this.getList();
  }

  private openModelDialog(row: OverOrOweRow) {
    this.currentRow = row;
    this.modelDialogVisible = true;
  }

  private handleProcess(row: OverOrOweRow) {
    this.selectedItem = row;
    this.showQuantity = true;
  }

  private handleProcessSuccess() {
    this.getList();
    AppModule.FetchOverOrOweCount();
  }

  private async getList() {
    this.loading = true;
    try {
      const params = {
        piCodeKeyword: this.piKeyword || undefined,
        sortDesc: this.sortDesc,
        pageNumber: this.table.pageNumber,
        pageSize: this.table.pageSize,
      };
      const res: any = await queryPendingProcessing(params);
      this.table.total = Number(res?.total || 0);
      this.table.list = res?.list || [];
    } catch (e) {
      this.table.total = 0;
      this.table.list = [];
    } finally {
      this.loading = false;
    }
  }
}
